import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { SortableTableHeader, useSortableData } from "../components/ui/sortable-table";
import { ApproveTransferModal } from "../components/transfers/ApproveTransferModal";
import { ChargeModal } from "../components/transfers/ChargeModal";
import { Search, CheckCircle, XCircle, Clock, User, Phone, Mail, PhoneCall, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
    fetchTransfers,
    approveTransfer,
    rejectTransfer,
    updateTransfer,
    deleteTransfer
} from "../services/crmServices/transferService";
import { fetchAvailableLeads, bulkUpdateLeads } from "../services/crmServices/leadService";
import { fetchDesks, fetchAvailableDesks } from "../services/crmServices/deskService";
import { searchUsers } from "../services/crmServices/userService";
import { getFullName } from "../services/common/util";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { useAuth } from "../hooks/useAuth";
import Swal from "sweetalert2";
import moment from "moment-timezone";

export function Transfers() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const navigate = useNavigate();
    const [transfers, setTransfers] = useState([]);
    const [allTransfers, setAllTransfers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [allSearchTerm, setAllSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("pending");
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [showChargeModal, setShowChargeModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [allCurrentPage, setAllCurrentPage] = useState(1);
    const [allPageSize, setAllPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [pendingLeads, setPendingLeads] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedPendingLead, setSelectedPendingLead] = useState(null);
    const [targetAgent, setTargetAgent] = useState("");
    const [agents, setAgents] = useState([]);
    const [assigning, setAssigning] = useState(false);

    useEffect(() => {
        if (user.position.role === "agent") {
            navigate("/");
        }
        initPage();
    }, []);

    const initPage = async () => {
        setLoading(true);
        try {
            const response = await fetchTransfers();
            if (response?.data?.length) {
                const pendingTransfers = response.data.filter((transfer) => transfer.status === "pending");
                let filteredPendingTransfers = [];
                let filteredAllTransfers = [];

                if (user.position.role === "deskManager") {
                    // Get desks managed by this desk manager
                    const desksResponse = await fetchDesks();
                    const managedDesks = desksResponse?.data?.filter(desk => {
                        const managerId = desk.manager?._id || desk.manager;
                        return managerId === user._id || managerId?.toString() === user._id?.toString();
                    }) || [];
                    
                    // Normalize desk IDs to strings for comparison
                    const managedDeskIds = managedDesks.map(desk => {
                        const deskId = desk._id?._id || desk._id;
                        return deskId?.toString();
                    }).filter(Boolean);
                    
                    // Helper function to normalize desk ID
                    const normalizeDeskId = (desk) => {
                        if (!desk) return null;
                        // Handle both populated object and ID string
                        if (typeof desk === 'string') return desk;
                        const deskId = desk._id || desk;
                        return deskId?.toString();
                    };
                    
                    // Helper to check if a desk ID matches any managed desk
                    const isManagedDesk = (deskId) => {
                        if (!deskId) return false;
                        const normalizedId = deskId?.toString();
                        return managedDeskIds.some(managedId => {
                            const normalizedManagedId = managedId?.toString();
                            return normalizedId === normalizedManagedId;
                        });
                    };
                    
                    // For pending transfers: show transfers FROM their desk OR TO their desk
                    // Note: Pending transfers typically don't have targetDesk set (it's set on approval)
                    // But we check it in case some transfers are created with targetDesk already set
                    filteredPendingTransfers = pendingTransfers.filter(
                        (transfer) => {
                            const leadDeskId = normalizeDeskId(transfer.lead?.desk);
                            const targetDeskId = normalizeDeskId(transfer.targetDesk);
                            
                            const isFromDesk = isManagedDesk(leadDeskId);
                            const isToDesk = targetDeskId ? isManagedDesk(targetDeskId) : false;
                            
                            // Show if: lead is from their desk OR transfer is to their desk
                            return isFromDesk || isToDesk;
                        }
                    );
                    
                    // Also check for approved transfers to their desk without agent assignment
                    // These should appear in pending leads, but let's also make sure they're not in pending transfers
                    // (they shouldn't be since they're approved, but just to be safe)

                    
                    // For all transfers (history): show transfers FROM their desk OR TO their desk
                    // Use originalDesk field to properly track where transfers came from
                    filteredAllTransfers = response.data.filter((transfer) => {
                        // Exclude pending transfers without agent assignment
                        if (transfer.status === "pending" && !transfer.targetAgent) {
                            return false;
                        }
                        
                        const originalDeskId = normalizeDeskId(transfer.originalDesk);
                        const targetDeskId = normalizeDeskId(transfer.targetDesk);
                        
                        // Show if transfer originated FROM their desk
                        const isFromDesk = isManagedDesk(originalDeskId);
                        // Show if transfer went TO their desk
                        const isToDesk = isManagedDesk(targetDeskId);
                        
                        // Exclude approved transfers to their desk without agent assignment (lead still has no agent)
                        // These should only appear in Pending Leads, not in history
                        if (transfer.status === "approved" && !transfer.targetAgent && isToDesk) {
                            // Check if lead still has no agent assigned
                            const leadAgent = transfer.lead?.agent?._id || transfer.lead?.agent;
                            if (!leadAgent) {
                                return false; // Exclude from history, it's in pending leads
                            }
                        }
                        
                        // Show transfers FROM their desk OR TO their desk
                        return isFromDesk || isToDesk;
                    });
                } else if (user.position.role === "floorManager") {
                    // Get desks managed by this floor manager (only conversion floor)
                    const desksResponse = await fetchDesks();
                    const managedDesks = desksResponse?.data?.filter(desk => {
                        const floorManagerId = desk.floorManager?._id || desk.floorManager;
                        return (
                            (floorManagerId === user._id || floorManagerId?.toString() === user._id?.toString()) &&
                            desk.floor === "conversion"
                        );
                    }) || [];
                    
                    // Normalize desk IDs to strings for comparison
                    const managedDeskIds = managedDesks.map(desk => {
                        const deskId = desk._id?._id || desk._id;
                        return deskId?.toString();
                    }).filter(Boolean);
                    
                    // Helper function to normalize desk ID
                    const normalizeDeskId = (desk) => {
                        if (!desk) return null;
                        // Handle both populated object and ID string
                        if (typeof desk === 'string') return desk;
                        const deskId = desk._id || desk;
                        return deskId?.toString();
                    };
                    
                    // Helper to check if a desk ID matches any managed desk
                    const isManagedDesk = (deskId) => {
                        if (!deskId) return false;
                        const normalizedId = deskId?.toString();
                        return managedDeskIds.some(managedId => {
                            const normalizedManagedId = managedId?.toString();
                            return normalizedId === normalizedManagedId;
                        });
                    };
                    
                    // For pending transfers: show transfers FROM their desks OR TO their desks
                    filteredPendingTransfers = pendingTransfers.filter(
                        (transfer) => {
                            const leadDeskId = normalizeDeskId(transfer.lead?.desk);
                            const targetDeskId = normalizeDeskId(transfer.targetDesk);
                            
                            const isFromDesk = isManagedDesk(leadDeskId);
                            const isToDesk = targetDeskId ? isManagedDesk(targetDeskId) : false;
                            
                            // Show if: lead is from their desk OR transfer is to their desk
                            return isFromDesk || isToDesk;
                        }
                    );
                    
                    // For all transfers (history): show transfers FROM their desks OR TO their desks
                    // Use originalDesk field to properly track where transfers came from
                    filteredAllTransfers = response.data.filter((transfer) => {
                        // Exclude pending transfers without agent assignment
                        if (transfer.status === "pending" && !transfer.targetAgent) {
                            return false;
                        }
                        
                        const originalDeskId = normalizeDeskId(transfer.originalDesk);
                        const targetDeskId = normalizeDeskId(transfer.targetDesk);
                        
                        // Show if transfer originated FROM their desks
                        const isFromDesk = isManagedDesk(originalDeskId);
                        // Show if transfer went TO their desks
                        const isToDesk = isManagedDesk(targetDeskId);
                        
                        // Exclude approved transfers to their desk without agent assignment (lead still has no agent)
                        // These should only appear in Pending Leads, not in history
                        if (transfer.status === "approved" && !transfer.targetAgent && isToDesk) {
                            // Check if lead still has no agent assigned
                            const leadAgent = transfer.lead?.agent?._id || transfer.lead?.agent;
                            if (!leadAgent) {
                                return false; // Exclude from history, it's in pending leads
                            }
                        }
                        
                        // Show transfers FROM their desks OR TO their desks
                        return isFromDesk || isToDesk;
                    });
                } else if (user.position.role === "generalManager") {
                    // Get desks managed by this general manager (all desks under their floor managers and desk managers)
                    // Use fetchAvailableDesks which filters based on user role
                    const desksResponse = await fetchAvailableDesks();
                    const managedDesks = desksResponse?.data || [];
                    
                    // Normalize desk IDs to strings for comparison
                    const managedDeskIds = managedDesks.map(desk => {
                        const deskId = desk._id?._id || desk._id;
                        return deskId?.toString();
                    }).filter(Boolean);
                    
                    // Helper function to normalize desk ID
                    const normalizeDeskId = (desk) => {
                        if (!desk) return null;
                        // Handle both populated object and ID string
                        if (typeof desk === 'string') return desk;
                        const deskId = desk._id || desk;
                        return deskId?.toString();
                    };
                    
                    // Helper to check if a desk ID matches any managed desk
                    const isManagedDesk = (deskId) => {
                        if (!deskId) return false;
                        const normalizedId = deskId?.toString();
                        return managedDeskIds.some(managedId => {
                            const normalizedManagedId = managedId?.toString();
                            return normalizedId === normalizedManagedId;
                        });
                    };
                    
                    // For pending transfers: show transfers FROM their desks OR TO their desks
                    filteredPendingTransfers = pendingTransfers.filter(
                        (transfer) => {
                            const leadDeskId = normalizeDeskId(transfer.lead?.desk);
                            const targetDeskId = normalizeDeskId(transfer.targetDesk);
                            
                            const isFromDesk = isManagedDesk(leadDeskId);
                            const isToDesk = targetDeskId ? isManagedDesk(targetDeskId) : false;
                            
                            // Show if: lead is from their desk OR transfer is to their desk
                            return isFromDesk || isToDesk;
                        }
                    );
                    
                    // For all transfers (history): show transfers FROM their desks OR TO their desks
                    // Use originalDesk field to properly track where transfers came from
                    filteredAllTransfers = response.data.filter((transfer) => {
                        // Exclude pending transfers without agent assignment
                        if (transfer.status === "pending" && !transfer.targetAgent) {
                            return false;
                        }
                        
                        const originalDeskId = normalizeDeskId(transfer.originalDesk);
                        const targetDeskId = normalizeDeskId(transfer.targetDesk);
                        
                        // Show if transfer originated FROM their desks
                        const isFromDesk = isManagedDesk(originalDeskId);
                        // Show if transfer went TO their desks
                        const isToDesk = isManagedDesk(targetDeskId);
                        
                        // Exclude approved transfers to their desk without agent assignment (lead still has no agent)
                        // These should only appear in Pending Leads, not in history
                        if (transfer.status === "approved" && !transfer.targetAgent && isToDesk) {
                            // Check if lead still has no agent assigned
                            const leadAgent = transfer.lead?.agent?._id || transfer.lead?.agent;
                            if (!leadAgent) {
                                return false; // Exclude from history, it's in pending leads
                            }
                        }
                        
                        // Show transfers FROM their desks OR TO their desks
                        return isFromDesk || isToDesk;
                    });
                } else {
                    // Admin sees all transfers
                    filteredPendingTransfers = pendingTransfers;
                    // For admins: exclude approved transfers without agent assignment (lead still has no agent)
                    // These should only appear in Pending Leads, not in history
                    filteredAllTransfers = response.data.filter((transfer) => {
                        // Exclude approved transfers without agent assignment where lead still has no agent
                        if (transfer.status === "approved" && !transfer.targetAgent) {
                            const leadAgent = transfer.lead?.agent?._id || transfer.lead?.agent;
                            if (!leadAgent) {
                                return false; // Exclude from history, it's in pending leads
                            }
                        }
                        return true;
                    });
                }

                setTransfers(filteredPendingTransfers);
                setAllTransfers(filteredAllTransfers);
            } else {
                setTransfers([]);
                setAllTransfers([]);
            }

            // Fetch pending leads (leads in manager's desk without agent assigned)
            await fetchPendingLeads();
        } catch (error) {
            console.error("Error fetching transfers:", error);
            toast.error("Failed to fetch transfers");
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingLeads = async () => {
        try {
            // Get all transfers to find approved ones that transferred leads to retention desks
            const transfersResponse = await fetchTransfers();
            if (!transfersResponse?.data?.length) {
                setPendingLeads([]);
                return;
            }

            // Get desks managed by this manager
            let managedDeskIds = [];
            
            if (user.position.role === "deskManager") {
                const desksResponse = await fetchDesks();
                const managedDesks = desksResponse?.data?.filter(desk => {
                    const managerId = desk.manager?._id || desk.manager;
                    return managerId === user._id || managerId?.toString() === user._id?.toString();
                }) || [];
                managedDeskIds = managedDesks.map(desk => {
                    const deskId = desk._id?._id || desk._id;
                    return deskId?.toString();
                }).filter(Boolean);
            } else if (user.position.role === "floorManager") {
                const desksResponse = await fetchDesks();
                const managedDesks = desksResponse?.data?.filter(desk => {
                    const floorManagerId = desk.floorManager?._id || desk.floorManager;
                    return floorManagerId === user._id || floorManagerId?.toString() === user._id?.toString();
                }) || [];
                managedDeskIds = managedDesks.map(desk => {
                    const deskId = desk._id?._id || desk._id;
                    return deskId?.toString();
                }).filter(Boolean);
            } else if (user.position.role === "generalManager") {
                const desksResponse = await fetchAvailableDesks();
                const managedDesks = desksResponse?.data || [];
                managedDeskIds = managedDesks.map(desk => {
                    const deskId = desk._id?._id || desk._id;
                    return deskId?.toString();
                }).filter(Boolean);
            } else if (user.position.role === "admin") {
                const desksResponse = await fetchDesks();
                managedDeskIds = desksResponse?.data?.map(desk => {
                    const deskId = desk._id?._id || desk._id;
                    return deskId?.toString();
                }).filter(Boolean) || [];
            }

            if (managedDeskIds.length === 0) {
                setPendingLeads([]);
                return;
            }

            // Helper function to normalize desk ID for comparison
            const normalizeDeskIdForComparison = (desk) => {
                if (!desk) return null;
                if (typeof desk === 'string') return desk;
                const deskId = desk._id || desk;
                return deskId?.toString();
            };

            // Find approved transfers where:
            // 1. Status is "approved"
            // 2. targetDesk is in manager's desks
            // 3. targetAgent is null (no agent was assigned during approval)
            const approvedTransfersWithoutAgent = transfersResponse.data.filter(
                (transfer) => {
                    if (transfer.status !== "approved" || transfer.targetAgent) {
                        return false;
                    }
                    const targetDeskId = normalizeDeskIdForComparison(transfer.targetDesk);
                    if (!targetDeskId) return false;
                    return managedDeskIds.includes(targetDeskId);
                }
            );

            // Get the lead IDs from these transfers
            const transferredLeadIds = approvedTransfersWithoutAgent
                .filter(transfer => transfer.lead) // Filter out transfers with null leads
                .map(
                    (transfer) => transfer.lead._id || transfer.lead
                );

            if (transferredLeadIds.length === 0) {
                setPendingLeads([]);
                return;
            }

            // Fetch the actual leads to check if they still have no agent
            const leadsResponse = await fetchAvailableLeads();
            if (leadsResponse?.data?.length) {
                // Filter to only show leads that:
                // 1. Were transferred (have an approved transfer record)
                // 2. Are in manager's desk
                // 3. Still have no agent assigned
                const filteredPendingLeads = leadsResponse.data.filter(
                    (lead) => {
                        if (!transferredLeadIds.includes(lead._id) || !lead.desk || lead.agent) {
                            return false;
                        }
                        const leadDeskId = normalizeDeskIdForComparison(lead.desk);
                        return leadDeskId && managedDeskIds.includes(leadDeskId);
                    }
                );

                setPendingLeads(filteredPendingLeads);
            } else {
                setPendingLeads([]);
            }
        } catch (error) {
            console.error("Error fetching pending leads:", error);
            setPendingLeads([]);
        }
    };

    // Combine transfers and pending leads for display
    const allPendingItems = [
        ...transfers.map(transfer => ({ type: 'transfer', data: transfer })),
        ...pendingLeads.map(lead => ({ type: 'pendingLead', data: lead }))
    ];

    const filteredTransfers = allPendingItems.filter(
        (item) => {
            if (item.type === 'transfer') {
                const transfer = item.data;
                // Skip transfers with null leads
                if (!transfer.lead) {
                    return false;
                }
                return (
                    transfer.lead?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    transfer.lead?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    transfer.lead?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    transfer.requestedBy?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    transfer.requestedBy?.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            } else {
                const lead = item.data;
                return (
                    lead.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.email?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }
        }
    );

    const filteredAllTransfers = allTransfers.filter(
        (transfer) => {
            // Skip transfers with null leads
            if (!transfer.lead) {
                return false;
            }
            return (
                transfer.lead?.firstName?.toLowerCase().includes(allSearchTerm.toLowerCase()) ||
                transfer.lead?.lastName?.toLowerCase().includes(allSearchTerm.toLowerCase()) ||
                transfer.lead?.email?.toLowerCase().includes(allSearchTerm.toLowerCase()) ||
                transfer.transferringAgent?.firstName?.toLowerCase().includes(allSearchTerm.toLowerCase()) ||
                transfer.transferringAgent?.lastName?.toLowerCase().includes(allSearchTerm.toLowerCase())
            );
        }
    );

    const { sortedData, sortConfig, handleSort } = useSortableData(filteredTransfers, "createdAt", "desc");
    const {
        sortedData: sortedAllData,
        sortConfig: allSortConfig,
        handleSort: handleAllSort
    } = useSortableData(filteredAllTransfers, "createdAt", "desc");

    const totalPages = Math.ceil(sortedData.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedTransfers = sortedData.slice(startIndex, startIndex + pageSize);

    const allTotalPages = Math.ceil(sortedAllData.length / allPageSize);
    const allStartIndex = (allCurrentPage - 1) * allPageSize;
    const allPaginatedTransfers = sortedAllData.slice(allStartIndex, allStartIndex + allPageSize);

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const handleAllPrevious = () => {
        setAllCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleAllNext = () => {
        setAllCurrentPage((prev) => Math.min(prev + 1, allTotalPages));
    };

    const handleApprove = (transfer) => {
        setSelectedTransfer(transfer);
        setShowApproveModal(true);
    };

    const handleReject = (transfer) => {
        Swal.fire({
            title: "Reject Transfer?",
            text: `Are you sure you want to reject the transfer of ${transfer.lead ? getFullName(transfer.lead) : 'this lead'}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, reject it!",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626"
        }).then((result) => {
            if (result.isConfirmed) {
                const rejectInfo = {
                    _id: transfer._id,
                    status: "rejected",
                    resolvedAt: moment().tz("America/New_York").toDate(),
                    resolvedBy: user._id
                };
                updateTransfer(rejectInfo).then((response) => {
                    if (response?.status === 200) {
                        toast.success("Transfer rejected!");
                        initPage();
                    } else {
                        toast.error(response?.data?.message || "Failed to reject transfer");
                    }
                });
            }
        });
    };

    const handleApproveSuccess = () => {
        setShowApproveModal(false);
        setSelectedTransfer(null);
        initPage();
    };

    const handleAssignAgent = (lead) => {
        setSelectedPendingLead(lead);
        setTargetAgent("");
        setAgents([]);
        
        // Fetch agents from the lead's desk
        if (lead.desk?._id || lead.desk) {
            const deskId = lead.desk._id || lead.desk;
            searchUsers({ agentDesk: deskId }).then((res) => {
                if (res.data) {
                    setAgents(res.data);
                }
            });
        }
        
        setShowAssignModal(true);
    };

    const handleAssignAgentSubmit = async () => {
        if (!targetAgent) {
            toast.error("Please select an agent");
            return;
        }

        setAssigning(true);
        try {
            const updateData = {
                leadIds: [selectedPendingLead._id],
                updates: {
                    agent: targetAgent
                }
            };

            const response = await bulkUpdateLeads(updateData);
            if (response?.status === 200) {
                toast.success("Lead assigned to agent successfully!");
                setShowAssignModal(false);
                setSelectedPendingLead(null);
                setTargetAgent("");
                initPage();
            } else {
                toast.error(response?.data?.message || "Failed to assign lead");
            }
        } catch (error) {
            console.error("Error assigning agent:", error);
            toast.error("Failed to assign lead to agent");
        } finally {
            setAssigning(false);
        }
    };

    const handleDeleteTransfer = (transfer) => {
        Swal.fire({
            title: "Delete Transfer?",
            text: `Are you sure you want to delete this transfer for ${getFullName(transfer.lead)}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await deleteTransfer({ _id: transfer._id });
                    if (response?.status === 200) {
                        toast.success("Transfer deleted successfully!");
                        initPage();
                    } else {
                        toast.error(response?.data?.message || "Failed to delete transfer");
                    }
                } catch (error) {
                    console.error("Error deleting transfer:", error);
                    toast.error("Failed to delete transfer");
                }
            }
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "new":
                return "bg-blue-100 text-blue-800";
            case "interested":
                return "bg-green-100 text-green-800";
            case "follow_up":
                return "bg-yellow-100 text-yellow-800";
            case "converted":
                return "bg-purple-100 text-purple-800";
            case "not_answered":
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getTransferStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "bg-yellow-100 text-yellow-800";
            case "approved":
                return "bg-green-100 text-green-800";
            case "rejected":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4">
            <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-1">Lead Transfers</h1>
                <p className="text-sm text-gray-600">
                    Review and approve lead transfers from agents in your managed desks.
                </p>

                <Tabs
                    value={activeTab}
                    onValueChange={(value) => {
                        setActiveTab(value);
                        initPage();
                    }}
                    className="w-full"
                >
                    <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                        <TabsTrigger value="pending" className="text-sm font-medium">
                            Pending ({transfers.length + pendingLeads.length})
                        </TabsTrigger>
                        <TabsTrigger value="all" className="text-sm font-medium">
                            All ({allTransfers.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="pending" className="space-y-3 mt-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="relative w-full sm:max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Search by lead name, email, or agent..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 h-9 text-sm"
                                />
                            </div>
                        </div>

                        {paginatedTransfers.length === 0 ? (
                            <div className="text-center py-8">
                                <Clock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-base font-medium text-gray-900 mb-1">No pending transfers</h3>
                                <p className="text-sm text-gray-500">
                                    {searchTerm
                                        ? "No transfers match your search criteria"
                                        : "All transfers have been processed"}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="rounded-lg border border-gray-200 shadow-sm overflow-x-auto bg-white">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-gray-50">
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Actions</TableHead>
                                                <SortableTableHeader
                                                    sortKey="lead.firstName"
                                                    sortConfig={sortConfig}
                                                    onSort={handleSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    Lead Name
                                                </SortableTableHeader>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Email</TableHead>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Phone</TableHead>
                                                <SortableTableHeader
                                                    sortKey="transferringAgent.firstName"
                                                    sortConfig={sortConfig}
                                                    onSort={handleSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    Agent
                                                </SortableTableHeader>
                                                <SortableTableHeader
                                                    sortKey="fromDesk.name"
                                                    sortConfig={sortConfig}
                                                    onSort={handleSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    From Desk
                                                </SortableTableHeader>
                                                <SortableTableHeader
                                                    sortKey="createdAt"
                                                    sortConfig={sortConfig}
                                                    onSort={handleSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    Date
                                                </SortableTableHeader>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Reason</TableHead>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Balance</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {paginatedTransfers.map((item) => {
                                                if (item.type === 'transfer') {
                                                    const transfer = item.data;
                                                    // Skip rendering if lead is null
                                                    if (!transfer.lead) {
                                                        return null;
                                                    }
                                                    return (
                                                        <TableRow key={`transfer-${transfer._id}`} className="hover:bg-gray-50">
                                                            <TableCell className="py-2">
                                                                <div className="flex space-x-1">
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => handleApprove(transfer)}
                                                                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs"
                                                                    >
                                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                                        Approve
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleReject(transfer)}
                                                                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-600 h-7 px-2 text-xs"
                                                                    >
                                                                        <XCircle className="h-3 w-3 mr-1" />
                                                                        Reject
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="font-medium text-sm py-2">
                                                                {transfer.lead ? getFullName(transfer.lead) : "-"}
                                                            </TableCell>
                                                            <TableCell className="text-xs text-gray-600 py-2">
                                                                {transfer.lead?.email || "-"}
                                                            </TableCell>
                                                            <TableCell className="text-xs text-gray-600 py-2">
                                                                {transfer.lead?.phone || "-"}
                                                            </TableCell>
                                                            <TableCell className="text-xs py-2">
                                                                {transfer.requestedBy ? getFullName(transfer.requestedBy) : "-"}
                                                            </TableCell>
                                                            <TableCell className="text-xs text-gray-600 py-2">
                                                                {transfer.lead?.desk?.name || "-"}
                                                            </TableCell>
                                                            <TableCell className="text-xs text-gray-500 py-2">
                                                                {moment(transfer.requestedAt).format("MMM D, HH:mm")}
                                                            </TableCell>
                                                            <TableCell className="max-w-48 truncate text-xs text-gray-600 py-2">
                                                                {transfer.requestedReason || "-"}
                                                            </TableCell>
                                                            <TableCell className="text-xs font-semibold text-green-600 py-2">
                                                                {transfer.moneyInBank 
                                                                    ? `$${transfer.moneyInBank.toLocaleString()}` 
                                                                    : "-"}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                } else {
                                                    const lead = item.data;
                                                    return (
                                                        <TableRow key={`pendingLead-${lead._id}`} className="bg-blue-50 hover:bg-blue-100">
                                                            <TableCell className="py-2">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleAssignAgent(lead)}
                                                                    className="h-7 px-2 text-xs"
                                                                >
                                                                    <User className="h-3 w-3 mr-1" />
                                                                    Assign
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell className="font-medium">
                                                                {getFullName(lead)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="space-y-1">
                                                                    {lead.email && (
                                                                        <div className="flex items-center text-sm">
                                                                            <Mail className="h-3 w-3 mr-1 text-gray-400" />
                                                                            {lead.email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="space-y-1">
                                                                    {lead.phone && (
                                                                        <div className="flex items-center text-sm">
                                                                            <PhoneCall className="h-3 w-3 mr-1 text-gray-400" />
                                                                            {lead.phone}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                                                                    Pending Assignment
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell>{lead.desk?.name || "-"}</TableCell>
                                                            <TableCell>
                                                                {lead.createdAt
                                                                    ? moment(lead.createdAt).format("YYYY-MM-DD HH:mm:ss")
                                                                    : "-"}
                                                            </TableCell>
                                                            <TableCell className="max-w-48 truncate">
                                                                Transferred to desk - awaiting agent assignment
                                                            </TableCell>
                                                            <TableCell>-</TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* Pagination */}
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-3 py-2 bg-gray-50 px-3 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600">Show</span>
                                        <select
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="border rounded px-2 py-1 text-xs bg-white"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                        <span className="text-xs text-gray-600">entries</span>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handlePrevious}
                                            disabled={currentPage === 1}
                                            className="h-7 px-3 text-xs"
                                        >
                                            Previous
                                        </Button>
                                        <span className="text-xs text-gray-600 font-medium">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleNext}
                                            disabled={currentPage === totalPages}
                                            className="h-7 px-3 text-xs"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="all" className="space-y-3 mt-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="relative w-full sm:max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Search by lead name, email, or agent..."
                                    value={allSearchTerm}
                                    onChange={(e) => setAllSearchTerm(e.target.value)}
                                    className="pl-10 h-9 text-sm"
                                />
                            </div>
                        </div>

                        {allPaginatedTransfers.length === 0 ? (
                            <div className="text-center py-8">
                                <Clock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-base font-medium text-gray-900 mb-1">No transfers found</h3>
                                <p className="text-sm text-gray-500">
                                    {allSearchTerm
                                        ? "No transfers match your search criteria"
                                        : "No transfers available"}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="rounded-lg border border-gray-200 shadow-sm overflow-x-auto bg-white">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-gray-50">
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Status</TableHead>
                                                <SortableTableHeader
                                                    sortKey="lead.firstName"
                                                    sortConfig={allSortConfig}
                                                    onSort={handleAllSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    Lead
                                                </SortableTableHeader>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Email</TableHead>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Phone</TableHead>
                                                <SortableTableHeader
                                                    sortKey="transferringAgent.firstName"
                                                    sortConfig={allSortConfig}
                                                    onSort={handleAllSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    From Agent
                                                </SortableTableHeader>
                                                <SortableTableHeader
                                                    sortKey="fromDesk.name"
                                                    sortConfig={allSortConfig}
                                                    onSort={handleAllSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    From Desk
                                                </SortableTableHeader>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">To Desk</TableHead>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">To Agent</TableHead>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Balance</TableHead>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Charge</TableHead>
                                                <SortableTableHeader
                                                    sortKey="createdAt"
                                                    sortConfig={allSortConfig}
                                                    onSort={handleAllSort}
                                                    className="py-2 text-xs font-semibold text-gray-700"
                                                >
                                                    Date
                                                </SortableTableHeader>
                                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Reason</TableHead>
                                                {user.position.role === "admin" && (
                                                    <TableHead className="py-2 text-xs font-semibold text-gray-700">Actions</TableHead>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {allPaginatedTransfers.map((transfer) => {
                                                // Skip rendering if lead is null
                                                if (!transfer.lead) {
                                                    return null;
                                                }
                                                return (
                                                <TableRow key={transfer._id} className="hover:bg-gray-50">
                                                    <TableCell className="py-2">
                                                        <Badge
                                                            variant="secondary"
                                                            className={`${getTransferStatusColor(transfer.status)} text-xs`}
                                                        >
                                                            {transfer.status.charAt(0).toUpperCase() +
                                                                transfer.status.slice(1)}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-medium text-sm py-2">
                                                        {transfer.lead ? getFullName(transfer.lead) : "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-600 py-2">
                                                        {transfer.lead?.email || "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-600 py-2">
                                                        {transfer.lead?.phone || "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs py-2">
                                                        {transfer.requestedBy ? getFullName(transfer.requestedBy) : "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-600 py-2">
                                                        {transfer.originalDesk?.name || transfer.lead?.desk?.name || "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-600 py-2">
                                                        {transfer.targetDesk?.name || "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-600 py-2">
                                                        {transfer.targetAgent ? getFullName(transfer.targetAgent) : "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs font-semibold text-green-600 py-2">
                                                        {transfer.moneyInBank 
                                                            ? `$${transfer.moneyInBank.toLocaleString()}` 
                                                            : "-"}
                                                    </TableCell>
                                                    <TableCell className="py-2">
                                                        {transfer.charges && transfer.charges.length > 0 ? (
                                                            <div className="flex items-center gap-1">
                                                                <span className="font-semibold text-green-600 text-sm">
                                                                    ${transfer.charges.reduce((sum, c) => sum + (c.amount || 0), 0).toLocaleString()}
                                                                </span>
                                                                {user.position.role === "admin" && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => {
                                                                            setSelectedTransfer(transfer);
                                                                            setShowChargeModal(true);
                                                                        }}
                                                                        className="text-xs h-5 px-1 text-blue-600 hover:text-blue-700"
                                                                        title={`${transfer.charges.length} charge(s) - Click to add more`}
                                                                    >
                                                                        ({transfer.charges.length})
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            user.position.role === "admin" ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => {
                                                                        setSelectedTransfer(transfer);
                                                                        setShowChargeModal(true);
                                                                    }}
                                                                    className="text-xs h-6 px-2"
                                                                >
                                                                    + Add
                                                                </Button>
                                                            ) : (
                                                                <span className="text-xs text-gray-400">-</span>
                                                            )
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-500 py-2">
                                                        {moment(transfer.requestedAt).format("MMM D, HH:mm")}
                                                    </TableCell>
                                                    <TableCell className="max-w-32 truncate text-xs text-gray-600 py-2" title={transfer.requestedReason}>
                                                        {transfer.requestedReason || "-"}
                                                    </TableCell>
                                                    {user.position.role === "admin" && (
                                                        <TableCell className="py-2">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleDeleteTransfer(transfer)}
                                                                className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* Pagination */}
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-3 py-2 bg-gray-50 px-3 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600">Show</span>
                                        <select
                                            value={allPageSize}
                                            onChange={(e) => {
                                                setAllPageSize(Number(e.target.value));
                                                setAllCurrentPage(1);
                                            }}
                                            className="border rounded px-2 py-1 text-xs bg-white"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                        <span className="text-xs text-gray-600">entries</span>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAllPrevious}
                                            disabled={allCurrentPage === 1}
                                            className="h-7 px-3 text-xs"
                                        >
                                            Previous
                                        </Button>
                                        <span className="text-xs text-gray-600 font-medium">
                                            Page {allCurrentPage} of {allTotalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAllNext}
                                            disabled={allCurrentPage === allTotalPages}
                                            className="h-7 px-3 text-xs"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            <ApproveTransferModal
                open={showApproveModal}
                onClose={() => {
                    setShowApproveModal(false);
                    setSelectedTransfer(null);
                }}
                onSuccess={handleApproveSuccess}
                transfer={selectedTransfer}
            />

            <ChargeModal
                open={showChargeModal}
                onClose={() => {
                    setShowChargeModal(false);
                    setSelectedTransfer(null);
                }}
                onSuccess={() => {
                    setShowChargeModal(false);
                    setSelectedTransfer(null);
                    initPage();
                }}
                transfer={selectedTransfer}
            />

            {/* Assign Agent Modal for Pending Leads */}
            {showAssignModal && selectedPendingLead && (
                <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>Assign Agent to Lead</DialogTitle>
                            <DialogDescription>
                                Assign {getFullName(selectedPendingLead)} to an agent in {selectedPendingLead.desk?.name || "the desk"}.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-medium mb-2">Lead Details</h4>
                                <div className="text-sm space-y-1">
                                    <p>
                                        <strong>Name:</strong> {getFullName(selectedPendingLead)}
                                    </p>
                                    <p>
                                        <strong>Email:</strong> {selectedPendingLead.email || "-"}
                                    </p>
                                    <p>
                                        <strong>Desk:</strong> {selectedPendingLead.desk?.name || "-"}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="targetAgent">Select Agent *</Label>
                                <Select
                                    options={agents.map((agent) => ({
                                        label: getFullName(agent),
                                        value: agent._id
                                    }))}
                                    value={targetAgent}
                                    onChange={setTargetAgent}
                                    placeholder={agents.length > 0 ? "Select agent" : "No agents available in this desk"}
                                    searchable
                                    disabled={agents.length === 0}
                                />
                                {agents.length === 0 && (
                                    <p className="text-xs text-red-500 mt-1">
                                        No agents available in this desk. Please add agents to the desk first.
                                    </p>
                                )}
                            </div>

                            <div className="flex space-x-3">
                                <Button
                                    onClick={handleAssignAgentSubmit}
                                    disabled={assigning || !targetAgent || agents.length === 0}
                                    className="flex-1"
                                >
                                    {assigning ? "Assigning..." : "Assign Agent"}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowAssignModal(false);
                                        setSelectedPendingLead(null);
                                        setTargetAgent("");
                                    }}
                                    disabled={assigning}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
